import { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useToc } from '../hooks/useToc';
import type { TocEntry } from '../api/toc';

/**
 * Persistent left sidebar.
 *
 * - Loads the full file/folder tree once on mount (cached via React Query).
 * - Top input filters the tree client-side: case-insensitive substring match.
 * - Folders are kept if they match or any descendant matches; matching subtrees auto-expand.
 */
export default function TocSidebar() {
  const { data, isLoading, error } = useToc();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!data) return null;
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return data;
    return { wiki: filterTree(data.wiki, trimmed), raw: filterTree(data.raw, trimmed) };
  }, [data, query]);

  return (
    <aside className="sidebar" data-testid="toc-sidebar">
      <div className="sidebar__filter">
        <input
          type="text"
          placeholder="Filter..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="toc-filter-input"
          aria-label="Filter table of contents"
        />
      </div>
      <div className="sidebar__scroll">
        {isLoading ? <div className="empty-state">Loading TOC...</div> : null}
        {error ? <div className="empty-state">Failed to load TOC.</div> : null}
        {filtered ? (
          <>
            <Section title="wiki" entries={filtered.wiki} forceOpen={Boolean(query.trim())} testId="toc-section-wiki" />
            <Section title="raw" entries={filtered.raw} forceOpen={Boolean(query.trim())} testId="toc-section-raw" />
          </>
        ) : null}
      </div>
    </aside>
  );
}

function Section({
  title,
  entries,
  forceOpen,
  testId,
}: {
  title: string;
  entries: TocEntry[];
  forceOpen: boolean;
  testId: string;
}) {
  return (
    <section className="sidebar__section" data-testid={testId}>
      <div className="sidebar__section-title">{title}</div>
      {entries.length === 0 ? (
        <div className="empty-state" style={{ fontSize: 12 }}>(empty)</div>
      ) : (
        <TocList entries={entries} forceOpen={forceOpen} />
      )}
    </section>
  );
}

function filterTree(entries: TocEntry[], needle: string): TocEntry[] {
  const out: TocEntry[] = [];
  for (const entry of entries) {
    if (entry.type === 'file') {
      if (entry.name.toLowerCase().includes(needle)) out.push(entry);
    } else {
      const matchedChildren = filterTree(entry.children, needle);
      if (entry.name.toLowerCase().includes(needle)) out.push(entry);
      else if (matchedChildren.length > 0) out.push({ ...entry, children: matchedChildren });
    }
  }
  return out;
}

function TocList({ entries, forceOpen }: { entries: TocEntry[]; forceOpen: boolean }) {
  return (
    <ul>
      {entries.map((entry) => (
        <TocNode key={entry.path} entry={entry} forceOpen={forceOpen} />
      ))}
    </ul>
  );
}

function TocNode({ entry, forceOpen }: { entry: TocEntry; forceOpen: boolean }) {
  const [open, setOpen] = useState(false);
  const expanded = forceOpen || open;

  if (entry.type === 'file') {
    return (
      <li data-testid={`toc-item-${entry.path}`}>
        <NavLink to={entry.path} end className={({ isActive }) => (isActive ? 'active' : '')}>
          {entry.name}
        </NavLink>
      </li>
    );
  }
  return (
    <li data-testid={`toc-folder-${entry.path}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={expanded}>
        <span style={{ display: 'inline-block', width: '1em', color: 'var(--text-subtle)' }}>
          {expanded ? '▾' : '▸'}
        </span>{' '}
        {entry.name}
      </button>
      {expanded ? <TocList entries={entry.children} forceOpen={forceOpen} /> : null}
    </li>
  );
}
